
import { Injectable } from '@angular/core';
import { EChartsOption } from 'echarts';

export interface NeckOrientation {
  timeMs: number;
  roll: number;
  pitch: number;
  yaw: number;
}

@Injectable({ providedIn: 'root' })
export class NeckAngleService {
  private readonly axisLeftRightLabel = $localize`:@@home.neckAngleCard.axis.leftRight:首の左右傾き (°)`;
  private readonly axisFrontBackLabel = $localize`:@@home.neckAngleCard.axis.frontBack:首の前後傾き (°)`;

  createChartOption(data: any[]): EChartsOption {
    const chartGrid = { left: 58, top: 20, right: 16, bottom: 48, containLabel: false };

    return {
      animation: false,
      grid: chartGrid,
      xAxis: [{
        scale: false,
        inverse: true,
        min: -90,
        max: 90,
        name: this.axisLeftRightLabel,
        nameLocation: 'middle',
        nameGap: 28,
        nameTextStyle: { fontSize: 10 },
        axisLabel: { fontSize: 9 },
        // 等間隔の格子は非表示。中央の基準線のみ markLine で表示する
        splitLine: { show: false },
      }],
      yAxis: [{
        scale: false,
        min: -90,
        max: 90,
        name: this.axisFrontBackLabel,
        nameLocation: 'middle',
        nameGap: 36,
        nameTextStyle: { fontSize: 10 },
        axisLabel: { fontSize: 9 },
        splitLine: { show: false },
      }],
      series: [{
        type: 'scatter',
        data,
        symbol: 'circle',
        symbolSize: 5,
        itemStyle: { color: 'rgb(255, 159, 64)', opacity: 0.4 },
        blendMode: 'darker',
        large: false,
        largeThreshold: 500,
        // 0°,0°（ニュートラル姿勢）の基準クロス。ライト/ダーク共通の中間グレー
        markLine: {
          silent: true,
          symbol: 'none',
          label: { show: false },
          lineStyle: {
            color: 'rgba(128, 128, 128, 0.5)',
            type: 'solid',
            width: 1,
          },
          data: [{ xAxis: 0 }, { yAxis: 0 }],
        },
      }],
    };
  }

  createCurrentChartOption(data: any[]): EChartsOption {
    const chartGrid = { left: 58, top: 20, right: 16, bottom: 48, containLabel: false };

    return {
      animation: false,
      grid: chartGrid,
      xAxis: [{ scale: false, inverse: true, show: false, min: -90, max: 90, splitLine: { show: false }, splitArea: { show: false } }],
      yAxis: [{ scale: false, show: false, min: -90, max: 90, splitLine: { show: false }, splitArea: { show: false } }],
      series: [{
        type: 'scatter',
        data,
        symbol: 'diamond',
        symbolSize: 15,
        itemStyle: {
          color: 'rgb(220, 20, 60)',
          opacity: 0.95,
          borderColor: '#ffffff',
          borderWidth: 2,
          shadowColor: 'rgba(0, 0, 0, 0.25)',
          shadowBlur: 4,
        },
      }],
    };
  }

  findClosestScatter(data: any[], currentTimeMs: number): any {
    if (!data.length) {
      return null;
    }

    let low = 0;
    let high = data.length - 1;
    let closestIndex = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const midTime = data[mid][2];

      if (midTime === currentTimeMs) {
        closestIndex = mid;
        break;
      } else if (midTime < currentTimeMs) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }

      if (Math.abs(midTime - currentTimeMs) < Math.abs(data[closestIndex][2] - currentTimeMs)) {
        closestIndex = mid;
      }
    }

    return data[closestIndex];
  }

  findClosestOrientation(data: NeckOrientation[], currentTimeMs: number): NeckOrientation | null {
    if (!data.length) {
      return null;
    }

    let low = 0;
    let high = data.length - 1;
    let closestIndex = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const midTime = data[mid].timeMs;

      if (midTime === currentTimeMs) {
        closestIndex = mid;
        break;
      } else if (midTime < currentTimeMs) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }

      if (Math.abs(midTime - currentTimeMs) < Math.abs(data[closestIndex].timeMs - currentTimeMs)) {
        closestIndex = mid;
      }
    }

    return data[closestIndex];
  }

  syncZoom(target: any, params: any): void {
    if (!target || !params?.batch) {
      return;
    }
    for (let i = 0; i < params.batch.length; i++) {
      const payload = params.batch[i];
      target.dispatchAction({
        type: 'dataZoom',
        dataZoomIndex: payload.dataZoomId,
        start: payload.start,
        end: payload.end,
        startValue: payload.startValue,
        endValue: payload.endValue,
        silent: true,
      });
    }
  }
}
